import { useMemo } from 'react';
import { ListContextProvider, useGetOne, useListController } from 'react-admin';
import { DataStats } from "../../models/data-stats";
import { SchoolsDataMap } from './components/schools-data-map';
import { SchoolsDataDistribution, SchoolDataDto } from '../../models/schools-data';
import { GlobalMapFilter } from '../../components/global-map-filter/global-map-filter';
import SchoolsFilter from './SchoolsFilter';
import { useDateRange } from '../../hooks/use-date-range';
import { useMapAttributeType } from '../../hooks/use-map-attribute';


const styles = {
  flex: { display: 'flex' },
  flexColumn: { display: 'flex', flexDirection: 'column' },
  leftCol: { flex: 1, },
  rightCol: { flex: 6, marginLeft: '0.5em' },
  singleCol: { marginTop: '0.5em', marginBottom: '0.5em' },
};

export type DataFilter = {
  type: 'sale' | 'rent';
  from: string,
  to: string;
}

export const SchoolsMapPage = () => {
  const [fromDate, toDate] = useDateRange();
  const [mapAttributeType] = useMapAttributeType();

  const dataFilter = useMemo<DataFilter>(() => {
    return {
      type: mapAttributeType.toString(),
      from: fromDate,
      to: toDate
    } as DataFilter;
  }, [fromDate, toDate, mapAttributeType]);

  const listContext = useListController<SchoolDataDto>({
    perPage: 9999,
    filter: {
      type: dataFilter.type,
      from: dataFilter.from,
      to: dataFilter.to
    }
  });
  const { data, isFetching } = listContext;

  const dataDistribution = useMemo<SchoolsDataDistribution>(() => {
    if (!data) return {};
    const aggregations = data
      .reduce(
        (stats: DataStats, data) => {
          if (stats.dataMin > data.dataMean)
            stats.dataMin = data.dataMean;
          if (stats.dataMax < data.dataMean)
            stats.dataMax = data.dataMean;
          if (data.dataCount > stats.dataCountMax)
            stats.dataCountMax = data.dataCount;
          stats.dataMean += data.dataMean;
          return stats;
        },
        {
          dataMin: data[0]?.dataMean ?? 0,
          dataMax: 0,
          dataMean: 0,
          dataCountMax: 0
        }
      );
    aggregations.dataMean /= data.length;
    return {
      data: data,
      stats: aggregations
    };
  }, [data]);

  return (
    <>
      <GlobalMapFilter dataStats={dataDistribution.stats!} mapDataType='schools' />
      <ListContextProvider value={listContext}>
        <div style={styles.flex}>
          <div style={styles.leftCol}>
            <div style={styles.singleCol}>
              <SchoolsFilter />
            </div>
          </div>
          <div style={styles.rightCol}>
            <div style={styles.singleCol}>
              <SchoolsDataMap dataDistribution={dataDistribution} isLoading={isFetching} />
            </div>
          </div>
        </div>
      </ListContextProvider>
    </>
  );
};
